import { getCessionsSummary } from 'lib/api/business';
import { CessionData } from 'lib/api/business/types';
import { BarChartData, LineChartData } from 'lib/types/shared';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

const months: { [key: string]: number } = {
  Enero: 1,
  Febrero: 2,
  Marzo: 3,
  Abril: 4,
  Mayo: 5,
  Junio: 6,
  Julio: 7,
  Agosto: 8,
  Septiembre: 9,
  Octubre: 10,
  Noviembre: 11,
  Diciembre: 12,
};

const orderedMonths = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function capitalize(str: string): string {
  if (str.length === 0) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function isCurrentYear(month: number, year: number): boolean {
  if (year === new Date().getFullYear()) {
    return true;
  } else {
    if (
      year === new Date().getFullYear() - 1 &&
      month > new Date().getMonth() + 1
    ) {
      return true;
    }
    return false;
  }
}

const useChart = (selected: string[]) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { data: cessionsData } = getCessionsSummary(dispatch, id);
  const [lineChartData, setLineChartData] = useState<LineChartData>([]);
  const [barChartData, setBarChartData] = useState<BarChartData>([]);

  const calculateLineChartData = () => {
    if (!cessionsData?.dataPayers) {
      setLineChartData([]);
      return;
    }

    const result: { [key: string]: CessionData[] } = cessionsData.dataPayers;
    const finalResult: {
      [key: string]: {
        'Factorizado ultimos 12 meses': number;
        'Factorizado ultimos 12-24 meses': number;
        'NC/Reclamo ultimos 12M': number;
        'NC/Reclamo 12-24M': number;
      };
    } = {};
    const all = selected.length === 0;

    for (const [key, value] of Object.entries(result)) {
      if (key === 'Últimos 12M' || key === 'Últimos 24M') {
        continue;
      }
      const month = capitalize(key.split('-')[0]);
      const year = parseInt(key.split('-')[1]);
      if (!finalResult[month]) {
        finalResult[month] = {
          'Factorizado ultimos 12 meses': 0,
          'Factorizado ultimos 12-24 meses': 0,
          'NC/Reclamo ultimos 12M': 0,
          'NC/Reclamo 12-24M': 0,
        };
      }
      for (const entry of value) {
        if (selected.includes(entry.identifier) || all) {
          if (isCurrentYear(months[month], year)) {
            finalResult[month]['Factorizado ultimos 12 meses'] += entry.amount;
            finalResult[month]['NC/Reclamo ultimos 12M'] +=
              entry.creditNoteAmount + entry.cancelAmount;
          } else {
            finalResult[month]['Factorizado ultimos 12-24 meses'] +=
              entry.amount;
            finalResult[month]['NC/Reclamo 12-24M'] +=
              entry.creditNoteAmount + entry.cancelAmount;
          }
        }
      }
    }

    const finalResultArray: LineChartData = Object.entries(finalResult).map(
      ([key, value]) => {
        return {
          name: key,
          ...value,
        };
      },
    );

    const currentMonth = new Date().getMonth() + 1;
    const currentMonthIndex = orderedMonths.indexOf(
      orderedMonths[currentMonth - 1],
    );
    const currentMonthsOrder = orderedMonths.slice(currentMonthIndex + 1);
    const previousMonthsOrder = orderedMonths.slice(0, currentMonthIndex + 1);
    const orderedMonthsArray = currentMonthsOrder.concat(previousMonthsOrder);
    finalResultArray.sort((a, b) => {
      return (
        orderedMonthsArray.indexOf(a.name) - orderedMonthsArray.indexOf(b.name)
      );
    });

    setLineChartData(finalResultArray);
  };

  const calculatedBarChartData = () => {
    const data = cessionsData?.dataFactorings?.['Últimos 24M'];
    if (!data) {
      setBarChartData([]);
      return;
    }

    const all = selected.length === 0;

    const payerNameMap = new Map<string, string>();
    const payersData = cessionsData?.dataPayers?.['Últimos 24M'];
    if (payersData) {
      payersData.forEach((payer) => {
        payerNameMap.set(payer.identifier, payer.name);
      });
    }

    const factoringsMap = new Map<string, Map<string, number>>();

    data.forEach((item) => {
      const factoringName = item.name;

      if (!factoringsMap.has(factoringName)) {
        factoringsMap.set(factoringName, new Map<string, number>());
      }

      const payersMap = factoringsMap.get(factoringName)!;

      if (item.payersData && item.payersData.length > 0) {
        item.payersData.forEach((payer) => {
          if (selected.includes(payer.identifier) || all) {
            const payerDisplayName =
              payerNameMap.get(payer.identifier) || payer.identifier;
            const currentAmount = payersMap.get(payerDisplayName) || 0;
            payersMap.set(payerDisplayName, currentAmount + payer.amount);
          }
        });
      } else {
        const totalKey = 'Total';
        const currentAmount = payersMap.get(totalKey) || 0;
        payersMap.set(totalKey, currentAmount + item.amount);
      }
    });

    const allPayers = new Set<string>();
    factoringsMap.forEach((payersMap) => {
      payersMap.forEach((_, payerName) => {
        allPayers.add(payerName);
      });
    });

    const MAX_SERIES = 20;
    const shouldGroupOthers = allPayers.size > MAX_SERIES;

    let topPayers: string[] = [];
    if (shouldGroupOthers) {
      const payerTotals = new Map<string, number>();
      allPayers.forEach((payerName) => {
        let total = 0;
        factoringsMap.forEach((payersMap) => {
          total += payersMap.get(payerName) || 0;
        });
        payerTotals.set(payerName, total);
      });

      const sortedPayers = Array.from(payerTotals.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([payerName]) => payerName);

      topPayers = sortedPayers.slice(0, MAX_SERIES);
    } else {
      topPayers = Array.from(allPayers);
    }

    const finalResult: BarChartData = [];

    factoringsMap.forEach((payersMap, factoringName) => {
      const factoringData: BarChartData[0] = {
        name: factoringName,
      };

      topPayers.forEach((payerName) => {
        factoringData[payerName] = payersMap.get(payerName) || 0;
      });

      if (shouldGroupOthers) {
        let othersAmount = 0;
        allPayers.forEach((payerName) => {
          if (!topPayers.includes(payerName)) {
            othersAmount += payersMap.get(payerName) || 0;
          }
        });
        if (othersAmount > 0) {
          factoringData['Otros'] = othersAmount;
        }
      }

      finalResult.push(factoringData);
    });

    finalResult.sort((a, b) => {
      const totalA = Object.keys(a)
        .filter((key) => key !== 'name')
        .reduce((sum, key) => sum + ((a[key] as number) || 0), 0);
      const totalB = Object.keys(b)
        .filter((key) => key !== 'name')
        .reduce((sum, key) => sum + ((b[key] as number) || 0), 0);
      return totalB - totalA;
    });

    setBarChartData(finalResult);
  };

  useEffect(() => {
    calculateLineChartData();
    calculatedBarChartData();
  }, [cessionsData, selected]);

  return { lineChartData, barChartData };
};

export default useChart;
